import { Card, CardContent } from "@/components/ui/card";
import { TrendingUp, TrendingDown, DollarSign } from "lucide-react";

interface JobMarginCardProps {
  estimatedTotal: number;
  actualTotal: number;
  margin: number;
  marginPct: number;
  estimatedLabor: number;
  estimatedMaterial: number;
  actualLabor: number;
  actualMaterial: number;
  actualSubcontractor: number;
  actualOther: number;
}

function fmt(n: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(n);
}

export function JobMarginCard({
  estimatedTotal,
  actualTotal,
  margin,
  marginPct,
  estimatedLabor,
  estimatedMaterial,
  actualLabor,
  actualMaterial,
  actualSubcontractor,
  actualOther,
}: JobMarginCardProps) {
  const isPositive = margin >= 0;
  const TrendIcon = isPositive ? TrendingUp : TrendingDown;
  const trendColor = isPositive ? "#16a34a" : "#dc2626";

  return (
    <Card>
      <CardContent className="p-4">
        {/* Margin headline */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="text-xs text-muted-foreground font-medium">Margin</p>
            <p
              className="text-2xl font-bold tabular-nums"
              style={{ color: trendColor }}
            >
              {fmt(margin)}
            </p>
          </div>
          <div
            className="flex items-center gap-1 px-2.5 py-1 rounded-full text-sm font-semibold"
            style={{
              color: trendColor,
              backgroundColor: isPositive ? "#dcfce7" : "#fee2e2",
            }}
          >
            <TrendIcon className="w-4 h-4" />
            {marginPct.toFixed(1)}%
          </div>
        </div>

        {/* Comparison grid */}
        <div className="grid grid-cols-3 gap-3 text-xs">
          {/* Header */}
          <div className="text-muted-foreground font-medium" />
          <div className="text-muted-foreground font-medium text-right">
            Estimated
          </div>
          <div className="text-muted-foreground font-medium text-right">
            Actual
          </div>

          {/* Labor */}
          <div className="text-muted-foreground">Labor</div>
          <div className="text-right tabular-nums">{fmt(estimatedLabor)}</div>
          <div className="text-right tabular-nums">{fmt(actualLabor)}</div>

          {/* Material */}
          <div className="text-muted-foreground">Materials</div>
          <div className="text-right tabular-nums">
            {fmt(estimatedMaterial)}
          </div>
          <div className="text-right tabular-nums">{fmt(actualMaterial)}</div>

          {/* Subcontractor (actual only) */}
          <div className="text-muted-foreground">Subs</div>
          <div className="text-right tabular-nums text-muted-foreground">—</div>
          <div className="text-right tabular-nums">
            {fmt(actualSubcontractor)}
          </div>

          {/* Other (actual only) */}
          {actualOther > 0 && (
            <>
              <div className="text-muted-foreground">Other</div>
              <div className="text-right tabular-nums text-muted-foreground">
                —
              </div>
              <div className="text-right tabular-nums">{fmt(actualOther)}</div>
            </>
          )}

          {/* Totals */}
          <div className="font-semibold border-t pt-1.5">Total</div>
          <div className="font-semibold border-t pt-1.5 text-right tabular-nums">
            {fmt(estimatedTotal)}
          </div>
          <div className="font-semibold border-t pt-1.5 text-right tabular-nums">
            {fmt(actualTotal)}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
